import {
  ActionManager,
  BoundingInfo,
  Color4,
  CreateBoxVertexData,
  ExecuteCodeAction,
  Matrix,
  Mesh,
  Nullable,
  PointerDragBehavior,
  Vector3,
  VertexBuffer,
} from '@babylonjs/core'
import { useDispatch, useSelector } from 'react-redux'
import store, { AppDispatch, RootState } from '../../store'
import { useCallback, useEffect } from 'react'
import { equalsWithEpsilon } from '../../utils/Common'
import {
  computeBoundingBox,
  createBimObjectTemplateMesh,
  createWallGaps,
  cutOpeningHolesInWallMesh,
  isBimObjectMesh,
  isDoorMesh,
  isSketchMesh,
  isWallMesh,
  isWindowMesh,
  removeBimObjectTemplateMesh,
  syncronizeBimObjectMeshWithTemplate,
} from '../utils'
import { editDoor } from '../../actions/construction'
import { updateHistory } from '../../actions/history'
import { ActionType } from '../../reducers/history'
import { SelectionManager } from '../selection/selectionManager'
import { SceneManager } from '../sceneManager'
import { SeparateElementUI } from './useSeparateWallTool'
import {
  isAntiParallel,
  isOrthoLinear,
  isParallel,
  rayBoundingBoxIntersection,
} from '../../utils/MathUtil'
import { createSelector } from '@reduxjs/toolkit'

const bimObjectsSelector = createSelector(
  [(state: RootState) => state.bim],
  (bim) => bim.objects,
)

export enum DoorProperty {
  Width = 'Width',
  Height = 'Height',
  CenterLocationOnWall = 'CenterLocationOnWall',
  SillHeight = 'SillHeight',
}

enum DoorAnchor {
  Start = 'Start',
  Center = 'Center',
  End = 'End',
}

type ChangeDoorPositionOptions = {
  centerLocationOnWall?: number
  sillHeight?: number
}

type SaveResetOptions = DoorProperty | DoorProperty[]

const calculateDoorProps = (doorMesh: Mesh, wallMesh: Mesh) => {
  let doorBoundingBox
  let shiftX
  let shiftY
  if (doorMesh.geometry) {
    doorBoundingBox = doorMesh.getBoundingInfo().boundingBox
    shiftX = doorMesh.position.x
    shiftY = doorMesh.position.y
  } else {
    const bbox = computeBoundingBox(doorMesh.getChildMeshes(), true)
    doorBoundingBox = new BoundingInfo(
      bbox.min,
      bbox.max,
      doorMesh.getWorldMatrix(),
    ).boundingBox
    const wallLocal = Vector3.TransformCoordinates(
      doorBoundingBox.centerWorld.clone(),
      wallMesh.getWorldMatrix().clone().invert(),
    )
    shiftX = wallLocal.x
    shiftY = wallLocal.y
  }
  const doorHalfWidth = doorBoundingBox.extendSize.x * doorMesh.scaling.x
  const doorHalfHeight = doorBoundingBox.extendSize.y * doorMesh.scaling.y
  const wallBoundingBox = wallMesh.getBoundingInfo().boundingBox
  const { x: wallHalfLength, y: wallHalfHeight } = wallBoundingBox.extendSize

  return {
    width: doorHalfWidth * 2,
    height: doorHalfHeight * 2,
    centerLocationOnWall: wallHalfLength + shiftX,
    sillHeight: wallHalfHeight - doorHalfHeight + shiftY,
  }
}

interface EditDoorUtils {
  readonly changeDoorWidth: (value: number, anchor?: DoorAnchor) => void
  readonly changeDoorHeight: (value: number, anchor?: DoorAnchor) => void
  readonly changeDoorPosition: (payload: ChangeDoorPositionOptions) => void
  readonly saveChanges: (options?: SaveResetOptions) => void
  readonly resetChanges: (options?: SaveResetOptions) => void
}

type DoorProperties = {
  [DoorProperty.CenterLocationOnWall]: number
  [DoorProperty.SillHeight]: number
  [DoorProperty.Width]: {
    value: number
    anchor: DoorAnchor
  }
  [DoorProperty.Height]: {
    value: number
    anchor: DoorAnchor
  }
}

const doorPropsMap = new Map<string, { count: number; props: DoorProperties }>()

export const useEditDoorTool = (
  doorMesh: Mesh,
  addAction: boolean = true,
): EditDoorUtils => {
  const dispatch = useDispatch<AppDispatch>()
  const bimObjects = useSelector(bimObjectsSelector)
  const bimObject = bimObjects.find(
    (bimObject) => bimObject.id === doorMesh.metadata.bimObjectId,
  )

  const changeDoorWidth = useCallback(
    (value: number, anchor: DoorAnchor = DoorAnchor.Center) => {
      const wallMesh =
        doorMesh.parent instanceof Mesh && isWallMesh(doorMesh.parent)
          ? (doorMesh.parent as Mesh)
          : undefined
      if (!isDoorMesh(doorMesh) || !wallMesh) {
        console.warn(
          `useEditDoorTool supports only meshes of 'Door' type with parent mesh of 'Wall' type`,
          {
            mesh: doorMesh,
            parent: doorMesh.parent,
          },
        )

        return
      }

      const doorProps = doorPropsMap.get(doorMesh.id)?.props
      if (!doorProps) {
        return
      }

      const newWidth = Math.max(value, 1e-3)

      let doorMeshWithGeometry
      if (!isBimObjectMesh(doorMesh)) {
        doorMeshWithGeometry = doorMesh
      } else {
        if (doorMesh.metadata.templateMeshId) {
          doorMeshWithGeometry = doorMesh
            .getScene()
            .getMeshById(doorMesh.metadata.templateMeshId) as Mesh
        }
      }
      if (!doorMeshWithGeometry) {
        doorMeshWithGeometry = createBimObjectTemplateMesh(doorMesh)
      }

      const doorBoundingBox = doorMeshWithGeometry.getBoundingInfo().boundingBox
      const {
        x: doorHalfWidth,
        y: doorHalfHeight,
        z: doorHalfThickness,
      } = doorBoundingBox.extendSize

      // No need to resize door if current width is equal to passed value
      if (equalsWithEpsilon(newWidth, doorHalfWidth * 2)) {
        return
      }

      const sketchMeshes = doorMeshWithGeometry
        .getChildMeshes(true, (node): node is Mesh => node instanceof Mesh)
        .reduce<Mesh[]>((acc, current) => {
          if (isSketchMesh(current)) {
            acc.push(current)
          }

          return acc
        }, [])

      const vertexData = CreateBoxVertexData({
        width: newWidth,
        height: doorHalfHeight * 2,
        depth: doorHalfThickness * 2,
        faceColors: [
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(0, 0, 0, 255),
          Color4.FromInts(255, 255, 255, 255),
        ],
      })

      vertexData.applyToMesh(doorMeshWithGeometry)

      if (anchor === DoorAnchor.Start || anchor === DoorAnchor.End) {
        const diff = newWidth - doorHalfWidth * 2
        doorMeshWithGeometry.position.x +=
          anchor === DoorAnchor.Start ? diff / 2 : -diff / 2
      }

      // Trigger Measurements UI position change
      doorMeshWithGeometry.computeWorldMatrix(true)

      const selectionManager = SelectionManager.getInstance()
      if (selectionManager.isSelected(doorMeshWithGeometry.id)) {
        // Update selection edges
        doorMesh.enableEdgesRendering(0.9999)
      }

      // Update Sketch mesh of window
      if (vertexData.positions) {
        const data = new Float32Array(vertexData.positions)
        sketchMeshes.forEach((sketchMesh) => {
          sketchMesh.setVerticesData(VertexBuffer.PositionKind, data)
        })
      }

      if (isBimObjectMesh(doorMesh) && doorMesh.metadata.templateMeshId) {
        syncronizeBimObjectMeshWithTemplate(doorMesh)
      }

      const openingMeshes = wallMesh.getChildMeshes(
        true,
        (node): node is Mesh =>
          node instanceof Mesh && (isWindowMesh(node) || isDoorMesh(node)),
      )
      cutOpeningHolesInWallMesh(wallMesh, openingMeshes, true)

      doorProps[DoorProperty.Width].anchor = anchor
    },
    [doorMesh],
  )

  const changeDoorHeight = useCallback(
    (value: number, anchor: DoorAnchor = DoorAnchor.Center) => {
      const wallMesh =
        doorMesh.parent instanceof Mesh && isWallMesh(doorMesh.parent)
          ? (doorMesh.parent as Mesh)
          : undefined
      if (!isDoorMesh(doorMesh) || !wallMesh) {
        console.warn(
          `useEditDoorTool supports only meshes of 'Door' type with parent mesh of 'Wall' type`,
          {
            mesh: doorMesh,
            parent: doorMesh.parent,
          },
        )

        return
      }

      const doorProps = doorPropsMap.get(doorMesh.id)?.props
      if (!doorProps) {
        return
      }

      const wallBoundingBox = wallMesh.getBoundingInfo().boundingBox
      const { y: wallHalfHeight } = wallBoundingBox.extendSize

      let doorMeshWithGeometry
      if (!isBimObjectMesh(doorMesh)) {
        doorMeshWithGeometry = doorMesh
      } else {
        if (doorMesh.metadata.templateMeshId) {
          doorMeshWithGeometry = doorMesh
            .getScene()
            .getMeshById(doorMesh.metadata.templateMeshId) as Mesh
        }
      }
      if (!doorMeshWithGeometry) {
        doorMeshWithGeometry = createBimObjectTemplateMesh(doorMesh)
      }

      const doorBoundingBox = doorMeshWithGeometry.getBoundingInfo().boundingBox
      const {
        x: doorHalfWidth,
        y: doorHalfHeight,
        z: doorHalfThickness,
      } = doorBoundingBox.extendSize

      const { sillHeight } = calculateDoorProps(doorMeshWithGeometry, wallMesh)
      let newHeight = Math.max(value ?? doorHalfHeight * 2, 1e-3)
      // Safe area (1e-3) need to avoid wall collapsing during CSG
      switch (anchor) {
        case DoorAnchor.Start:
        case DoorAnchor.Center:
        case DoorAnchor.End:
          // Max door height should be limited to upper side of the wall
          newHeight = Math.min(
            newHeight,
            wallHalfHeight * 2 - sillHeight - 1e-3,
          )
          break
      }

      // No need to resize door if current height is equal to passed value
      if (equalsWithEpsilon(newHeight, doorHalfHeight * 2)) {
        return
      }

      const sketchMeshes = doorMeshWithGeometry
        .getChildMeshes(true, (node): node is Mesh => node instanceof Mesh)
        .reduce<Mesh[]>((acc, current) => {
          if (isSketchMesh(current)) {
            acc.push(current)
          }

          return acc
        }, [])

      const vertexData = CreateBoxVertexData({
        width: doorHalfWidth * 2,
        height: newHeight,
        depth: doorHalfThickness * 2,
        faceColors: [
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(255, 255, 255, 255),
          Color4.FromInts(0, 0, 0, 255),
          Color4.FromInts(255, 255, 255, 255),
        ],
      })

      vertexData.applyToMesh(doorMeshWithGeometry)

      doorMeshWithGeometry.position.y =
        -wallHalfHeight + newHeight / 2 + sillHeight

      // Trigger Measurements UI position change
      doorMeshWithGeometry.computeWorldMatrix(true)

      const selectionManager = SelectionManager.getInstance()
      if (selectionManager.isSelected(doorMeshWithGeometry.id)) {
        // Update selection edges
        doorMesh.enableEdgesRendering(0.9999)
      }

      // Update Sketch mesh of window
      if (vertexData.positions) {
        const data = new Float32Array(vertexData.positions)
        sketchMeshes.forEach((sketchMesh) => {
          sketchMesh.setVerticesData(VertexBuffer.PositionKind, data)
        })
      }

      if (isBimObjectMesh(doorMesh) && doorMesh.metadata.templateMeshId) {
        syncronizeBimObjectMeshWithTemplate(doorMesh)
      }

      const openingMeshes = wallMesh.getChildMeshes(
        true,
        (node): node is Mesh =>
          node instanceof Mesh && (isWindowMesh(node) || isDoorMesh(node)),
      )
      cutOpeningHolesInWallMesh(wallMesh, openingMeshes, true)

      doorProps[DoorProperty.Height].anchor = anchor
    },
    [doorMesh],
  )

  const changeDoorPosition = useCallback(
    (payload: ChangeDoorPositionOptions) => {
      const wallMesh =
        doorMesh.parent instanceof Mesh && isWallMesh(doorMesh.parent)
          ? (doorMesh.parent as Mesh)
          : undefined
      if (!isDoorMesh(doorMesh) || !wallMesh) {
        console.warn(
          `useEditDoorTool supports only meshes of 'Door' type with parent mesh of 'Wall' type`,
          {
            mesh: doorMesh,
            parent: doorMesh.parent,
          },
        )

        return
      }

      let doorMeshWithGeometry
      if (!isBimObjectMesh(doorMesh)) {
        doorMeshWithGeometry = doorMesh
      } else {
        if (doorMesh.metadata.templateMeshId) {
          doorMeshWithGeometry = doorMesh
            .getScene()
            .getMeshById(doorMesh.metadata.templateMeshId) as Mesh
        }
      }
      if (!doorMeshWithGeometry) {
        doorMeshWithGeometry = createBimObjectTemplateMesh(doorMesh)
      }

      const wallBoundingBox = wallMesh.getBoundingInfo().boundingBox
      const { x: wallHalfLength, y: wallHalfHeight } =
        wallBoundingBox.extendSize

      const { height, centerLocationOnWall, sillHeight } = calculateDoorProps(
        doorMeshWithGeometry,
        wallMesh,
      )
      const newCenterLocationOnWall =
        payload.centerLocationOnWall ?? centerLocationOnWall
      // Limit sill height to be within wall boundaries
      const newSillHeight = Math.max(
        Math.min(payload.sillHeight ?? sillHeight, wallHalfHeight * 2 - height),
        0,
      )

      // No need to move door if current center location on wall and sill height is equal to passed values
      if (
        equalsWithEpsilon(newCenterLocationOnWall, centerLocationOnWall) &&
        equalsWithEpsilon(newSillHeight, sillHeight)
      ) {
        return
      }

      doorMeshWithGeometry.position.x =
        -wallHalfLength + newCenterLocationOnWall
      doorMeshWithGeometry.position.y =
        -wallHalfHeight + height / 2 + newSillHeight

      // Trigger Measurements UI position change
      doorMeshWithGeometry.computeWorldMatrix(true)

      if (isBimObjectMesh(doorMesh) && doorMesh.metadata.templateMeshId) {
        syncronizeBimObjectMeshWithTemplate(doorMesh)
      }

      const openingMeshes = wallMesh.getChildMeshes(
        true,
        (node): node is Mesh =>
          node instanceof Mesh && (isWindowMesh(node) || isDoorMesh(node)),
      )
      cutOpeningHolesInWallMesh(wallMesh, openingMeshes, true)
    },
    [doorMesh],
  )

  const saveChanges = useCallback(
    (option?: SaveResetOptions) => {
      const wallMesh =
        doorMesh.parent instanceof Mesh && isWallMesh(doorMesh.parent)
          ? (doorMesh.parent as Mesh)
          : undefined
      if (!isDoorMesh(doorMesh) || !wallMesh) {
        console.warn(
          `useEditDoorTool supports only meshes of 'Door' type with parent mesh of 'Wall' type`,
          {
            mesh: doorMesh,
            parent: doorMesh.parent,
          },
        )

        return
      }

      const doorProps = doorPropsMap.get(doorMesh.id)?.props
      if (!doorProps) {
        return
      }

      const savedData = {
        width: doorProps[DoorProperty.Width].value,
        height: doorProps[DoorProperty.Height].value,
        centerLocationOnWall: doorProps[DoorProperty.CenterLocationOnWall],
        sillHeight: doorProps[DoorProperty.SillHeight],
      }
      const options = option ? (Array.isArray(option) ? option : [option]) : []

      const { width, height, centerLocationOnWall, sillHeight } =
        calculateDoorProps(doorMesh, wallMesh)
      if (!options.length || options.includes(DoorProperty.Width)) {
        savedData.width = width
      }

      if (!options.length || options.includes(DoorProperty.Height)) {
        savedData.height = height
        savedData.sillHeight = sillHeight
      }

      if (
        !options.length ||
        options.includes(DoorProperty.CenterLocationOnWall)
      ) {
        savedData.centerLocationOnWall = centerLocationOnWall
      }

      if (!options.length || options.includes(DoorProperty.SillHeight)) {
        savedData.sillHeight = sillHeight
      }

      if (
        !equalsWithEpsilon(
          doorProps[DoorProperty.Width].value,
          savedData.width,
        ) ||
        !equalsWithEpsilon(
          doorProps[DoorProperty.Height].value,
          savedData.height,
        ) ||
        !equalsWithEpsilon(
          doorProps[DoorProperty.CenterLocationOnWall],
          savedData.centerLocationOnWall,
        ) ||
        !equalsWithEpsilon(
          doorProps[DoorProperty.SillHeight],
          savedData.sillHeight,
        )
      ) {
        const { width, height, centerLocationOnWall, sillHeight } = savedData
        dispatch(
          editDoor({
            width,
            height,
            id: doorMesh.id,
            wall_id: wallMesh.id,
            sill_height: sillHeight,
            center_location_on_wall: centerLocationOnWall,
          }),
        )

        const convertData = store.getState().construction.convertData
        dispatch(
          updateHistory({
            convertData,
            items: [
              {
                itemId: doorMesh.id,
                actionType: ActionType.EDIT,
              },
            ],
          }),
        )

        doorProps[DoorProperty.Width].value = width
        doorProps[DoorProperty.Height].value = height
        doorProps[DoorProperty.CenterLocationOnWall] = centerLocationOnWall
        doorProps[DoorProperty.SillHeight] = sillHeight

        createWallGaps()
      }
    },
    [doorMesh, dispatch],
  )

  const resetChanges = useCallback(
    (option?: SaveResetOptions) => {
      if (!isDoorMesh(doorMesh)) {
        console.warn(
          `useEditDoorTool supports only meshes of 'Door' type with parent mesh of 'Wall' type`,
          {
            mesh: doorMesh,
            parent: doorMesh.parent,
          },
        )

        return
      }

      const doorProps = doorPropsMap.get(doorMesh.id)?.props
      if (!doorProps) {
        return
      }

      const options = option ? (Array.isArray(option) ? option : [option]) : []

      if (!options.length || options.includes(DoorProperty.Width)) {
        const { value, anchor } = doorProps[DoorProperty.Width]
        changeDoorWidth(value, anchor)
      }

      if (!options.length || options.includes(DoorProperty.Height)) {
        const { value, anchor } = doorProps[DoorProperty.Height]
        changeDoorHeight(value, anchor)
      }

      if (
        !options.length ||
        options.includes(DoorProperty.CenterLocationOnWall) ||
        options.includes(DoorProperty.SillHeight)
      ) {
        const shouldResetCenterLocationOnWall =
          !options.length || options.includes(DoorProperty.CenterLocationOnWall)
        const shouldResetSillHeight =
          !options.length || options.includes(DoorProperty.SillHeight)

        changeDoorPosition({
          centerLocationOnWall: shouldResetCenterLocationOnWall
            ? doorProps[DoorProperty.CenterLocationOnWall]
            : undefined,
          sillHeight: shouldResetSillHeight
            ? doorProps[DoorProperty.SillHeight]
            : undefined,
        })
      }
    },
    [doorMesh, changeDoorWidth, changeDoorHeight, changeDoorPosition],
  )

  useEffect(() => {
    const wallMesh =
      doorMesh.parent instanceof Mesh && isWallMesh(doorMesh.parent)
        ? (doorMesh.parent as Mesh)
        : undefined
    if (!isDoorMesh(doorMesh) || !wallMesh) {
      console.warn(
        `useEditDoorTool supports only meshes of 'Door' type with parent mesh of 'Wall' type`,
        {
          mesh: doorMesh,
          parent: doorMesh.parent,
        },
      )

      return
    }

    if (!doorPropsMap.has(doorMesh.id)) {
      const { width, height, centerLocationOnWall, sillHeight } =
        calculateDoorProps(doorMesh, wallMesh)

      doorPropsMap.set(doorMesh.id, {
        count: 1,
        props: {
          [DoorProperty.Width]: {
            value: width,
            anchor: DoorAnchor.Center,
          },
          [DoorProperty.Height]: {
            value: height,
            anchor: DoorAnchor.Center,
          },
          [DoorProperty.CenterLocationOnWall]: centerLocationOnWall,
          [DoorProperty.SillHeight]: sillHeight,
        },
      })
    } else {
      const obj = doorPropsMap.get(doorMesh.id)
      if (obj) {
        obj.count += 1
      }
    }

    let templateMesh: Nullable<Mesh> = null
    let action: Nullable<ExecuteCodeAction> = null
    if (addAction) {
      const localMatrix = new Matrix()
      const tmpVec = new Vector3()
      action = new ExecuteCodeAction(
        ActionManager.OnPickDownTrigger,
        ({ sourceEvent }) => {
          const isCtrlPressed = sourceEvent.ctrlKey
          const scene = SceneManager.getInstance().getScene()
          const isSeparateWallUIVisible = SeparateElementUI.isUIVisible
          if (isCtrlPressed || !scene || isSeparateWallUIVisible) {
            return
          }

          const transform = templateMesh
            ? templateMesh.getWorldMatrix()
            : doorMesh.getWorldMatrix()
          const doorDirection = Vector3.TransformNormalFromFloatsToRef(
            1,
            0,
            0,
            transform,
            new Vector3(),
          )
          const doorUp = Vector3.TransformNormalFromFloatsToRef(
            0,
            1,
            0,
            transform,
            new Vector3(),
          )
          const doorNormal = Vector3.TransformNormalFromFloatsToRef(
            0,
            0,
            1,
            transform,
            new Vector3(),
          )

          const ray = scene.createPickingRay(
            scene.pointerX,
            scene.pointerY,
            null,
            scene.activeCamera,
          )
          const doorBoundingBox = templateMesh
            ? templateMesh.getBoundingInfo().boundingBox
            : doorMesh.getBoundingInfo().boundingBox
          const { pickNormal, pickPoint } =
            rayBoundingBoxIntersection(ray, doorBoundingBox) ?? {}
          if (!pickNormal || !pickPoint) {
            return
          }

          transform.invertToRef(localMatrix)

          let anchor: Nullable<DoorAnchor> = null

          // Door Width Change
          if (isOrthoLinear(pickNormal, doorDirection)) {
            const isFromBeginning = isAntiParallel(pickNormal, doorDirection)
            anchor = isFromBeginning ? DoorAnchor.End : DoorAnchor.Start
          }
          // Door Height Change
          else if (isParallel(pickNormal, doorUp)) {
            anchor = DoorAnchor.Start
          }
          // Window Position Change or Resize
          else if (isOrthoLinear(pickNormal, doorNormal)) {
            const edgePoint = Math.min(
              doorBoundingBox.extendSize.x * 0.25,
              doorBoundingBox.extendSize.y * 0.25,
            )

            Vector3.TransformCoordinatesToRef(pickPoint, localMatrix, tmpVec)

            const isInTheMiddleX =
              Math.abs(tmpVec.x) <= doorBoundingBox.extendSize.x - edgePoint
            const isInTheMiddleY =
              tmpVec.y <= doorBoundingBox.extendSize.y - edgePoint
            if (!isInTheMiddleX || !isInTheMiddleY) {
              if (Math.abs(tmpVec.x) > Math.abs(tmpVec.y)) {
                pickNormal.set(tmpVec.x > 0 ? 1 : -1, 0, 0)
                Vector3.TransformNormalToRef(pickNormal, transform, pickNormal)

                const isFromBeginning = isAntiParallel(
                  pickNormal,
                  doorDirection,
                )
                anchor = isFromBeginning ? DoorAnchor.End : DoorAnchor.Start
              } else {
                pickNormal.copyFrom(doorUp)
                anchor = DoorAnchor.Start
              }
            }
          } else {
            return
          }

          const pointerDragBehavior = new PointerDragBehavior(
            anchor ? { dragAxis: pickNormal } : { dragPlaneNormal: doorNormal },
          )
          pointerDragBehavior.useObjectOrientationForDragging = false
          pointerDragBehavior.moveAttached = false

          let totalDragDistance = 0
          const gridSettings = store.getState().construction.gridSettings
          const cellSize = gridSettings.snapToGrid ? gridSettings.cellSize : 1
          const { width, height, centerLocationOnWall } = calculateDoorProps(
            templateMesh ? templateMesh : doorMesh,
            wallMesh,
          )
          pointerDragBehavior.onDragObservable.add(({ delta }) => {
            Vector3.TransformNormalToRef(delta, localMatrix, tmpVec)

            if (anchor) {
              if (!isBimObjectMesh(doorMesh) || !bimObject?.isNonEditable) {
                if (Math.abs(tmpVec.x) > 0) {
                  totalDragDistance += tmpVec.x

                  let newWidth =
                    anchor === DoorAnchor.Start
                      ? width + totalDragDistance
                      : width - totalDragDistance

                  if (gridSettings.snapToGrid) {
                    newWidth = Math.round(newWidth / cellSize) * cellSize
                  }

                  changeDoorWidth(newWidth, anchor)
                } else if (Math.abs(tmpVec.y) > 0) {
                  totalDragDistance += tmpVec.y

                  let newHeight =
                    anchor === DoorAnchor.Start
                      ? height + totalDragDistance
                      : height - totalDragDistance

                  if (gridSettings.snapToGrid) {
                    newHeight = Math.round(newHeight / cellSize) * cellSize
                  }

                  changeDoorHeight(newHeight, anchor)
                }
              }
            } else {
              totalDragDistance += tmpVec.x

              let newCenterLocationOnWall =
                centerLocationOnWall + totalDragDistance

              if (gridSettings.snapToGrid) {
                newCenterLocationOnWall =
                  Math.round(newCenterLocationOnWall / cellSize) * cellSize
              }

              changeDoorPosition({
                centerLocationOnWall: newCenterLocationOnWall,
              })
            }
          })

          pointerDragBehavior.onDragEndObservable.add(() => {
            if (isBimObjectMesh(doorMesh) && templateMesh) {
              templateMesh.removeBehavior(pointerDragBehavior)
            } else {
              doorMesh.removeBehavior(pointerDragBehavior)
            }

            saveChanges()
          })

          if (isBimObjectMesh(doorMesh) && templateMesh) {
            templateMesh.addBehavior(pointerDragBehavior)
          } else {
            doorMesh.addBehavior(pointerDragBehavior)
          }
        },
      )

      if (isBimObjectMesh(doorMesh)) {
        templateMesh = createBimObjectTemplateMesh(doorMesh)
        templateMesh.actionManager?.registerAction(action)
      } else {
        doorMesh.actionManager?.registerAction(action)
      }
    }

    return () => {
      if (action) {
        if (isBimObjectMesh(doorMesh) && templateMesh) {
          templateMesh.actionManager?.unregisterAction(action)
          removeBimObjectTemplateMesh(doorMesh)
        } else {
          doorMesh.actionManager?.unregisterAction(action)
        }
      }

      const obj = doorPropsMap.get(doorMesh.id)
      if (obj) {
        obj.count -= 1
        if (!obj.count) {
          const { width, height, centerLocationOnWall, sillHeight } =
            calculateDoorProps(doorMesh, wallMesh)
          if (
            !equalsWithEpsilon(obj.props[DoorProperty.Width].value, width) ||
            !equalsWithEpsilon(obj.props[DoorProperty.Height].value, height) ||
            !equalsWithEpsilon(
              obj.props[DoorProperty.CenterLocationOnWall],
              centerLocationOnWall,
            ) ||
            !equalsWithEpsilon(obj.props[DoorProperty.SillHeight], sillHeight)
          ) {
            resetChanges()
          }
          doorPropsMap.delete(doorMesh.id)
        }
      }
    }
  }, [
    doorMesh,
    addAction,
    changeDoorWidth,
    changeDoorHeight,
    changeDoorPosition,
    saveChanges,
    resetChanges,
  ])

  return {
    changeDoorWidth,
    changeDoorHeight,
    changeDoorPosition,
    saveChanges,
    resetChanges,
  }
}
